import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";
import { Send, MessageCircle } from "lucide-react";

export default function MessagesPage() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [recruiter, setRecruiter] = useState(null);
  const scrollRef = useRef();

  const loadThreads = async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data.items || []);
    // load recruiter for quick-start
    if (user?.role !== "admin") {
      try {
        const r = await api.get("/messages/recruiter");
        setRecruiter(r.data);
      } catch {}
    }
  };

  const loadThread = async (threadId) => {
    const { data } = await api.get(`/messages/thread/${threadId}`);
    setMessages(data.items || []);
    setSelected(threadId);
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
  };

  useEffect(() => { loadThreads(); }, []);

  const send = async () => {
    if (!text.trim()) return;
    let to_user_id = null;
    if (selected) {
      const t = threads.find(x => x.thread_id === selected);
      to_user_id = t?.from_user_id === user.id ? t.to_user_id : t?.from_user_id;
    } else if (recruiter) {
      to_user_id = recruiter.user_id;
    }
    if (!to_user_id) return;
    await api.post("/messages", { to_user_id, body: text, thread_id: selected });
    setText("");
    await loadThreads();
    if (selected) loadThread(selected);
    else loadThread([user.id, to_user_id].sort().join("-"));
  };

  const startWithRecruiter = async () => {
    if (!recruiter) return;
    const tid = [user.id, recruiter.user_id].sort().join("-");
    setSelected(tid);
    setMessages([]);
  };

  return (
    <div>
      <div className="mb-6">
        <div className="eyebrow mb-2">Communication</div>
        <h1 className="font-display text-3xl font-bold text-navy">Messages</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-6 h-[70vh]">
        <div data-testid={TID.messagesThreadList} className="border border-slate-200 rounded-xl bg-white overflow-y-auto">
          <div className="p-3 border-b flex justify-between items-center">
            <div className="text-sm font-semibold text-navy">Conversations</div>
            {recruiter && user?.role !== "admin" && (
              <button onClick={startWithRecruiter} className="text-xs text-navy hover:text-gold">Message recruiter</button>
            )}
          </div>
          {threads.map(t => {
            const other = t.from_user_id === user.id ? t.to_name : t.from_name;
            const role = t.from_user_id === user.id ? t.to_role : t.from_role;
            return (
              <button key={t.thread_id} onClick={() => loadThread(t.thread_id)}
                className={`w-full text-left p-3 border-b hover:bg-slate-50 ${selected === t.thread_id ? "bg-slate-50" : ""}`}>
                <div className="text-sm font-medium text-navy">{other}</div>
                <div className="text-[11px] text-slate-400 uppercase">{role}</div>
                <div className="text-xs text-slate-500 truncate mt-0.5">{t.body}</div>
              </button>
            );
          })}
          {threads.length === 0 && <div className="p-6 text-center text-sm text-slate-500">
            <MessageCircle className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            No conversations yet.
          </div>}
        </div>

        <div className="md:col-span-2 border border-slate-200 rounded-xl bg-white flex flex-col">
          <div ref={scrollRef} className="flex-1 p-5 overflow-y-auto space-y-3">
            {messages.map(m => {
              const mine = m.from_user_id === user.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-navy text-white" : "bg-slate-100 text-slate-800"}`}>
                    <div className="text-[10px] uppercase opacity-70 mb-0.5">{m.from_name}</div>
                    <div>{m.body}</div>
                  </div>
                </div>
              );
            })}
            {selected && messages.length === 0 && <div className="text-slate-500 text-sm text-center">Say hi 👋</div>}
            {!selected && <div className="text-slate-500 text-sm text-center">Select a conversation, or {recruiter && user?.role !== "admin" ? <button onClick={startWithRecruiter} className="text-navy font-medium">message your recruiter</button> : "wait for messages"}.</div>}
          </div>
          <div className="border-t p-3 flex gap-2">
            <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
              data-testid={TID.messagesInput}
              placeholder="Write a message…"
              className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm" />
            <button onClick={send} data-testid={TID.messagesSend} className="btn-navy py-2 px-4"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
